import type { DataJobValidationResult } from '../types/data-jobs.types';

export type ValidationMatrixCell = {
  result: DataJobValidationResult;
  status: string;
};

export type ValidationMatrixRow = {
  key: string;
  platform: string;
  table: string;
  cells: Record<string, ValidationMatrixCell | undefined>;
};

export type ValidationMatrix = {
  checkTypes: string[];
  rows: ValidationMatrixRow[];
};

const STATUS_RANK: Record<string, number> = {
  fail: 3,
  failed: 3,
  warning: 2,
  warn: 2,
  pass: 1,
  success: 1,
};

function metricsRecord(
  result: DataJobValidationResult
): Record<string, unknown> {
  return result.metrics ?? {};
}

/** Resolve platform (datasource) from a validation result. */
export function resolveValidationPlatform(result: DataJobValidationResult): string {
  if (result.datasource) {
    return result.datasource;
  }
  const metrics = metricsRecord(result);
  if (typeof metrics.platform === 'string' && metrics.platform) {
    return metrics.platform;
  }
  if (typeof metrics.datasource === 'string' && metrics.datasource) {
    return metrics.datasource;
  }
  const target = result.target ?? '';
  if (target.includes('.')) {
    return target.split('.', 1)[0];
  }
  return target || 'unknown';
}

/** Resolve table / connector name from a validation result. */
export function resolveValidationTable(result: DataJobValidationResult): string {
  if (result.connector_name) {
    return result.connector_name;
  }
  const metrics = metricsRecord(result);
  if (typeof metrics.connector_name === 'string' && metrics.connector_name) {
    return metrics.connector_name;
  }
  if (typeof metrics.connector === 'string' && metrics.connector) {
    return metrics.connector;
  }
  const target = result.target ?? '';
  if (target.includes('.')) {
    return target.split('.', 1)[1] || '—';
  }
  return '—';
}

export function formatCheckTypeLabel(checkType: string): string {
  return checkType
    .split('__')
    .map((part) => part.replace(/_/g, ' '))
    .join(' · ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function worstValidationStatus(statuses: string[]): string | null {
  let worst: string | null = null;
  let worstRank = 0;
  for (const status of statuses) {
    const rank = STATUS_RANK[status.toLowerCase()] ?? 0;
    if (rank > worstRank) {
      worstRank = rank;
      worst = status;
    }
  }
  return worst;
}

/**
 * Pivot mart-style validation rows into a platform + table × check matrix.
 * Prefer mart_verification results; if none, use all results with a parseable
 * platform.table target (excludes account-level reconciliation rows when mart
 * results exist).
 */
export function buildValidationMatrix(
  results: DataJobValidationResult[]
): ValidationMatrix {
  const martResults = results.filter(
    (result) => result.check_source === 'mart_verification'
  );
  const source = martResults.length > 0
    ? martResults
    : results.filter((result) => {
      const platform = resolveValidationPlatform(result);
      const table = resolveValidationTable(result);
      return Boolean(platform) && table !== '—' && !/^\d+$/.test(table);
    });

  const checkTypes = Array.from(
    new Set(source.map((result) => result.check_type))
  ).sort((left, right) => left.localeCompare(right));

  const rowMap = new Map<string, ValidationMatrixRow>();

  for (const result of source) {
    const platform = resolveValidationPlatform(result);
    const table = resolveValidationTable(result);
    const key = `${platform}::${table}`;
    let row = rowMap.get(key);
    if (!row) {
      row = { key, platform, table, cells: {} };
      rowMap.set(key, row);
    }

    const existing = row.cells[result.check_type];
    if (!existing) {
      row.cells[result.check_type] = {
        result,
        status: result.status,
      };
      continue;
    }

    const worst = worstValidationStatus([existing.status, result.status]);
    if (worst && worst.toLowerCase() === result.status.toLowerCase()) {
      row.cells[result.check_type] = {
        result,
        status: result.status,
      };
    }
  }

  const rows = Array.from(rowMap.values()).sort((left, right) => {
    const platformCompare = left.platform.localeCompare(right.platform);
    if (platformCompare !== 0) {
      return platformCompare;
    }
    return left.table.localeCompare(right.table);
  });

  return { checkTypes, rows };
}

/** Platform × metric matrix for account-level reconciliation results. */
export function buildReconciliationMatrix(
  results: DataJobValidationResult[]
): ValidationMatrix {
  const reconResults = results.filter(
    (result) => result.check_source === 'platform_reconciliation'
  );

  const checkTypes = Array.from(
    new Set(reconResults.map((result) => result.check_type))
  ).sort((left, right) => left.localeCompare(right));

  const rowMap = new Map<string, ValidationMatrixRow>();
  const cellResults = new Map<string, Map<string, DataJobValidationResult[]>>();

  for (const result of reconResults) {
    const platform = resolveValidationPlatform(result);
    const key = `${platform}::accounts`;
    let row = rowMap.get(key);
    if (!row) {
      row = { key, platform, table: 'accounts', cells: {} };
      rowMap.set(key, row);
    }

    let byCheck = cellResults.get(key);
    if (!byCheck) {
      byCheck = new Map();
      cellResults.set(key, byCheck);
    }
    const bucket = byCheck.get(result.check_type) ?? [];
    bucket.push(result);
    byCheck.set(result.check_type, bucket);
  }

  for (const [rowKey, byCheck] of cellResults.entries()) {
    const row = rowMap.get(rowKey);
    if (!row) continue;

    for (const [checkType, bucket] of byCheck.entries()) {
      const statuses = bucket.map((item) => item.status);
      const worst = worstValidationStatus(statuses) ?? bucket[0].status;
      const representative = bucket.find(
        (item) => item.status.toLowerCase() === worst.toLowerCase()
      ) ?? bucket[0];

      row.cells[checkType] = {
        result: representative,
        status: worst,
      };
    }
  }

  const rows = Array.from(rowMap.values()).sort((left, right) => (
    left.platform.localeCompare(right.platform)
  ));

  return { checkTypes, rows };
}

export function collectReconciliationCellResults(
  results: DataJobValidationResult[],
  platform: string,
  checkType: string
): DataJobValidationResult[] {
  return results.filter((result) => (
    result.check_source === 'platform_reconciliation'
    && resolveValidationPlatform(result) === platform
    && result.check_type === checkType
  ));
}
